import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { VesselRedundancyGroup } from '../../dto/vessel-redundancy-group';
import { Vessel } from '../../dto/vessel';
import { VesselRedundancyGroupDetailsComponent } from '../vessel-redundancy-group-details/vessel-redundancy-group-details.component';
import { Subject, takeUntil } from 'rxjs';
import { VesselRedundancyGroupService } from '../../service/vessel-redundancy-group.service';

@Component({ 
  selector: 'dpi-vessel-redundancy-group-list',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule, MatTooltipModule, NgIf],
  templateUrl: './vessel-redundancy-group-list.component.html',
  styleUrl: './vessel-redundancy-group-list.component.scss'
})
export class VesselRedundancyGroupListComponent implements OnInit, OnDestroy {
  vessel: Vessel;
  displayedColumns: string[] = ['name', 'description'];
  dataSource: VesselRedundancyGroup[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private vesselRedundancyGroupService: VesselRedundancyGroupService) {
    this.vessel = this.notifyService.getSelectedVessel();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.vesselSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.vessel = model;
        this.reloadVesselRedundancyGroup(model);
      })

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reloadVesselRedundancyGroup(vessel: Vessel) {
    this.vesselRedundancyGroupService.getAllRedundancyGroupOfVessel(vessel.id)
    .pipe(takeUntil(this.destroyed$))
    .subscribe({
      next: (res: any) => {
        this.dataSource = res;
        this.listSize.emit(this.dataSource.length);
      },
      error: (error: any) => {
        console.error('Error: ', error);
        this.dataSource = [];
      }
    })
  }

  selectRedundancyGroup(selected: VesselRedundancyGroup) {
    if (!this.isReadonly) {
      var objCopy: VesselRedundancyGroup = { ...selected };
      const dialogRef = this._dialog.open(VesselRedundancyGroupDetailsComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.redundancyGroup);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.redundancyGroup.id);
          }
        });
    }
  }
  
   //--- CRUD actions ---
  
    create() {
      this.vesselRedundancyGroupService.createRedundancyGroup(this.vessel.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: model => {
            this.addNewModelToDataSource(model);
            this.selectRedundancyGroup(model);
          },
          error: err => { console.log(err); }
        });
    }
  
    private addNewModelToDataSource(model: VesselRedundancyGroup) {
      let list: VesselRedundancyGroup[] = [];
      for (var item of this.dataSource) {
        list.push(item);
      }
      list.push(model);
      this.dataSource = list;
      this.listSize.emit(this.dataSource.length);
    }
  
    update(model: VesselRedundancyGroup) {
      this.vesselRedundancyGroupService.updateRedundancyGroup(model)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: res => {
            this.replaceModelInDataSource(res);
          },
          error: err => { console.log(err); }
        });
    }
  
    private replaceModelInDataSource(model: VesselRedundancyGroup) {
      let list: VesselRedundancyGroup[] = [];
      for (var item of this.dataSource) {
        if (item.id === model.id) {
          list.push(model);
        } else {
          list.push(item);
        }
      }
      this.dataSource = list;
      this.listSize.emit(this.dataSource.length);
    }
  
    delete(modelId: number) {
      this.vesselRedundancyGroupService.deleteRedundancyGroup(modelId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: res => {
            this.removeModelFromDataSource(modelId);
          },
          error: err => { console.log(err); }
        });
    }
  
    private removeModelFromDataSource(modelId: number) {
      let list: VesselRedundancyGroup[] = [];
      for (var item of this.dataSource) {
        if (item.id === modelId) {
          //the model is to be removed --> do nothing
        } else {
          list.push(item);
        }
      }
      this.dataSource = list;
      this.listSize.emit(this.dataSource.length);
    }
  
}
